import React, { createContext, useContext, useState, ReactNode } from 'react';
import { PublicForm } from '../models/PublicForm';
import { ViewState } from '../models/ViewState';
import FirestoreRepository from '../services/FirestoreRepository';
import FirebaseService from '../services/FirebaseService';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

/** Public form reads require a signed-in (anonymous) user, and a first-time
    visitor's anonymous sign-in finishes after the first load attempt — so wait
    for a user (up to `timeoutMs`) before touching Firestore. */
function waitForSignedInUser(timeoutMs = 10000): Promise<boolean> {
  const auth = getAuth();
  if (auth.currentUser) return Promise.resolve(true);
  return new Promise((resolve) => {
    const timer = setTimeout(() => { unsubscribe(); resolve(!!auth.currentUser); }, timeoutMs);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) return;
      clearTimeout(timer);
      unsubscribe();
      resolve(true);
    });
  });
}

interface ContentContextType {
  forms: PublicForm[];
  viewState: ViewState;
  addForm: (form: PublicForm) => void;
  setViewState: (state: ViewState) => void;
  getFormById: (id: string) => PublicForm | undefined;
  loadFormById: (id: string) => Promise<PublicForm | null>;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

interface ContentProviderProps {
  children: ReactNode;
}

export const ContentProvider: React.FC<ContentProviderProps> = ({ children }) => {
  const [forms, setForms] = useState<PublicForm[]>([]);
  const [viewState, setViewState] = useState<ViewState>({ type: 'LOADING' });
  const [isRepositoryReady, setIsRepositoryReady] = useState(false);
  const repositoryRef = React.useRef<FirestoreRepository | null>(null);

  // Initialize repository after ensuring Firebase is ready
  React.useEffect(() => {
    const initRepository = async () => {
      try {
        // Wait for FirebaseService to be initialized first
        const firebaseService = FirebaseService.getInstance();
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait

        while (!firebaseService.isInitialized() && attempts < maxAttempts) {
          console.log(`ContentContext: Waiting for Firebase to initialize... (attempt ${attempts + 1})`);
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }

        if (!firebaseService.isInitialized()) {
          console.error('ContentContext: Firebase failed to initialize after 5 seconds');
          // Still try to initialize repository as a fallback
        }

        console.log('ContentContext: Firebase is ready, initializing FirestoreRepository...');
        repositoryRef.current = new FirestoreRepository();
        setIsRepositoryReady(true);
        console.log('ContentContext: FirestoreRepository initialized successfully');
      } catch (error) {
        console.error('ContentContext: Failed to initialize FirestoreRepository, retrying...', error);
        // Retry after a short delay if Firebase isn't ready yet
        setTimeout(initRepository, 100);
      }
    };
    initRepository();
  }, []);

  const addForm = (form: PublicForm) => {
    setForms(prevForms => {
      // Check if form already exists
      const exists = prevForms.some(f => f.id === form.id);
      if (exists) {
        // Replace the existing form
        return prevForms.map(f => f.id === form.id ? form : f);
      } else {
        // Add the new form
        return [...prevForms, form];
      }
    });
  };

  const getFormById = (id: string) => {
    console.log('ContentContext: getFormById called with:', id);
    console.log('ContentContext: Available forms:', forms.map(f => ({ id: f.id, title: f.title })));
    
    const form = forms.find(form => form.id === id);
    console.log('ContentContext: getFormById result:', form ? { id: form.id, title: form.title } : 'not found');
    
    return form;
  };

  const loadFormById = async (id: string): Promise<PublicForm | null> => {
    try {
      console.log('🔄 ContentContext: loadFormById initiated for:', id);
      console.log('ContentContext: Current forms in state:', forms.map(f => f.id));
      console.log('ContentContext: Repository ready:', !!repositoryRef.current);

      // Wait for Firebase to be ready first
      const firebaseService = FirebaseService.getInstance();
      let firebaseAttempts = 0;
      while (!firebaseService.isInitialized() && firebaseAttempts < 50) {
        console.log(`⏳ ContentContext: Waiting for Firebase... (attempt ${firebaseAttempts + 1})`);
        await new Promise(resolve => setTimeout(resolve, 100));
        firebaseAttempts++;
      }

      // Wait for repository to be ready
      if (!repositoryRef.current) {
        console.log('⏳ ContentContext: Repository not ready, waiting...');
        // Wait up to 3 seconds for repository to initialize
        for (let i = 0; i < 30; i++) {
          await new Promise(resolve => setTimeout(resolve, 100));
          if (repositoryRef.current) break;
        }
        if (!repositoryRef.current) {
          console.error('❌ ContentContext: Repository failed to initialize');
          setViewState({ type: 'NETWORK_ERROR' });
          return null;
        }
      }

      // Check if form already exists in state
      const existingForm = getFormById(id);
      if (existingForm) {
        console.log('✅ ContentContext: Form found in state cache');
        setViewState({ type: 'FORM_LOADED', form: existingForm });
        return existingForm;
      }

      setViewState({ type: 'LOADING' });

      // Also check if the form exists with a Public_Form_ prefix
      if (!id.startsWith('Public_Form_')) {
        console.log('ContentContext: Checking for form with Public_Form_ prefix');
        const existingFormWithPrefix = getFormById(`Public_Form_${id}`);
        if (existingFormWithPrefix) {
          console.log('✅ ContentContext: Form found in state with prefix');
          setViewState({ type: 'FORM_LOADED', form: existingFormWithPrefix });
          return existingFormWithPrefix;
        }
      }

      if (!(await waitForSignedInUser())) {
        console.warn('⚠️ ContentContext: No signed-in user yet; form read may be denied');
      }

      // Load form from Firestore directly with ID
      console.log('🔍 ContentContext: Form not in cache, loading from Firestore...');
      const form = await repositoryRef.current.getPublicForm(id);

      if (form) {
        console.log('✅ ContentContext: Form loaded successfully from Firestore');
        console.log('ContentContext: Adding form to state cache');
        addForm(form);
        setViewState({ type: 'FORM_LOADED', form });
        return form;
      } else {
        console.log('❌ ContentContext: Form not found in Firestore');
        setViewState({ type: 'FORM_NOT_FOUND' });
        return null;
      }
    } catch (error) {
      console.error('ContentContext: Error loading form:', error);
      setViewState({ type: 'NETWORK_ERROR' });
      return null;
    }
  };

  return (
    <ContentContext.Provider
      value={{
        forms,
        viewState,
        addForm,
        setViewState,
        getFormById,
        loadFormById,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = (): ContentContextType => {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};